import { useEffect, useRef, useState, useCallback } from 'react'

/**
 * Polling visibility-aware: il polling si ferma quando il tab non e' visibile.
 *
 * - Avvia immediatamente al mount (se visible) con una fetch + setInterval
 * - Su visibilitychange:
 *   - hidden  -> clearInterval (no fetch in background)
 *   - visible -> fetch immediata (recupero dati persi) + setInterval
 * - Cleanup garantito su unmount (clearInterval + removeEventListener)
 * - `enabled=false` ferma tutto (utile per pausa manuale o errori auth)
 *
 * NOTA: il fetcher passato dovrebbe usare api.silent.* per evitare logout su 401.
 */
export function useVisiblePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs: number,
  enabled: boolean = true,
) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Ref alla fetcher per evitare di re-creare l'effect a ogni re-render del parent
  const fetcherRef = useRef(fetcher)
  useEffect(() => { fetcherRef.current = fetcher }, [fetcher])

  const tick = useCallback(async () => {
    try {
      const result = await fetcherRef.current()
      setData(result); setError(null)
    } catch (e) {
      setError(e as Error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    const start = () => {
      if (intervalRef.current) return  // gia' attivo (idempotente)
      tick()
      intervalRef.current = setInterval(tick, intervalMs)
    }
    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') start()
      else stop()
    }

    if (document.visibilityState === 'visible') start()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [intervalMs, enabled, tick])

  return { data, error, isLoading, refetch: tick }
}
